import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  MessageSquare,
  Mic,
  Volume2,
  Database,
  FileText,
  HardDrive,
  FolderOpen,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Copy,
  Check,
} from 'lucide-react';
import { NeMoDataset, NeMoTaskType, NeMoDatasetFormat } from '@/mock/gpu-instance';

interface NeMoDatasetManagerProps {
  datasets: NeMoDataset[];
  selectedDatasetId?: string;
  onSelectDataset?: (dataset: NeMoDataset) => void;
  onViewDatasetCard?: (dataset: NeMoDataset) => void;
  filterTask?: NeMoTaskType;
}

const getTaskIcon = (task: NeMoTaskType) => {
  switch (task) {
    case 'llm':
      return <MessageSquare className="h-4 w-4 text-green-500" />;
    case 'asr':
      return <Mic className="h-4 w-4 text-blue-500" />;
    case 'tts':
      return <Volume2 className="h-4 w-4 text-purple-500" />;
  }
};

const getTaskLabel = (task: NeMoTaskType) => {
  switch (task) {
    case 'llm':
      return 'LLM Fine-tuning';
    case 'asr':
      return 'Speech Recognition';
    case 'tts':
      return 'Text-to-Speech';
  }
};

const getStatusBadge = (status: NeMoDataset['status']) => {
  switch (status) {
    case 'ready':
      return (
        <Badge className="bg-green-500/10 text-green-600 border-green-500/30">
          <CheckCircle2 className="h-3 w-3 mr-1" /> Ready
        </Badge>
      );
    case 'processing':
      return (
        <Badge className="bg-yellow-500/10 text-yellow-600 border-yellow-500/30">
          <Loader2 className="h-3 w-3 mr-1 animate-spin" /> Processing
        </Badge>
      );
    case 'error':
      return (
        <Badge className="bg-red-500/10 text-red-600 border-red-500/30">
          <AlertCircle className="h-3 w-3 mr-1" /> Error
        </Badge>
      );
  }
};

const getFormatBadge = (format: NeMoDatasetFormat) => {
  switch (format) {
    case 'jsonl':
      return (
        <Badge className="bg-green-500/10 text-green-600 border-green-500/30">
          <FileText className="h-3 w-3 mr-1" /> JSONL
        </Badge>
      );
    case 'json_manifest':
      return (
        <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/30">
          <FolderOpen className="h-3 w-3 mr-1" /> JSON Manifest
        </Badge>
      );
  }
};

const NeMoDatasetManager = ({
  datasets,
  selectedDatasetId,
  onSelectDataset,
  onViewDatasetCard,
  filterTask,
}: NeMoDatasetManagerProps) => {
  const [copiedPath, setCopiedPath] = useState<string | null>(null);
  const [taskFilter, setTaskFilter] = useState<string>(filterTask || 'all');
  const [formatFilter, setFormatFilter] = useState<string>('all');

  const handleCopyPath = async (path: string) => {
    try {
      await navigator.clipboard.writeText(path);
      setCopiedPath(path);
      setTimeout(() => setCopiedPath(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Filter datasets
  const filteredDatasets = datasets.filter((dataset) => {
    const matchesTask = taskFilter === 'all' || dataset.task === taskFilter;
    const matchesFormat = formatFilter === 'all' || dataset.format === formatFilter;
    return matchesTask && matchesFormat;
  });

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex gap-4">
        {!filterTask && (
          <Select value={taskFilter} onValueChange={setTaskFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by task" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Tasks</SelectItem>
              <SelectItem value="llm">LLM Fine-tuning</SelectItem>
              <SelectItem value="asr">Speech Recognition</SelectItem>
              <SelectItem value="tts">Text-to-Speech</SelectItem>
            </SelectContent>
          </Select>
        )}

        <Select value={formatFilter} onValueChange={setFormatFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by format" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Formats</SelectItem>
            <SelectItem value="jsonl">JSONL</SelectItem>
            <SelectItem value="json_manifest">JSON Manifest</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Dataset List */}
      <div className="space-y-3">
        {filteredDatasets.map((dataset) => (
          <Card
            key={dataset.id}
            className={`cursor-pointer transition-all hover:border-primary/50 ${
              selectedDatasetId === dataset.id ? 'border-primary ring-1 ring-primary' : ''
            } ${dataset.status !== 'ready' ? 'opacity-60' : ''}`}
            onClick={() => onViewDatasetCard?.(dataset)}
          >
            <CardContent className="pt-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-muted">
                    <Database className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-medium flex items-center gap-2">
                      {dataset.displayName}
                      {getStatusBadge(dataset.status)}
                    </div>
                    <div className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
                      {getTaskIcon(dataset.task)}
                      <span>{getTaskLabel(dataset.task)}</span>
                    </div>
                  </div>
                </div>
                {getFormatBadge(dataset.format)}
              </div>

              {/* Description */}
              <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                {dataset.description}
              </p>

              {/* Stats */}
              <div className="grid grid-cols-4 gap-3 mb-3">
                <div className="p-2 bg-muted/50 rounded text-center">
                  <div className="text-xs text-muted-foreground">Train</div>
                  <div className="font-mono font-medium text-sm">
                    {dataset.trainCount.toLocaleString()}
                  </div>
                </div>
                <div className="p-2 bg-muted/50 rounded text-center">
                  <div className="text-xs text-muted-foreground">Val</div>
                  <div className="font-mono font-medium text-sm">
                    {dataset.valCount.toLocaleString()}
                  </div>
                </div>
                <div className="p-2 bg-muted/50 rounded text-center">
                  <div className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                    <HardDrive className="h-3 w-3" />
                    Size
                  </div>
                  <div className="font-mono font-medium text-sm">{dataset.totalSize}</div>
                </div>
                <div className="p-2 bg-muted/50 rounded text-center">
                  <div className="text-xs text-muted-foreground">Language</div>
                  <div className="font-mono font-medium text-sm">
                    {dataset.language?.toUpperCase() || '-'}
                  </div>
                </div>
              </div>

              {/* Path */}
              <div className="flex items-center justify-between bg-muted/50 p-2 rounded">
                <code className="text-xs text-muted-foreground truncate">
                  {dataset.path}
                </code>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 w-6 p-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopyPath(dataset.path);
                  }}
                >
                  {copiedPath === dataset.path ? (
                    <Check className="h-3 w-3" />
                  ) : (
                    <Copy className="h-3 w-3" />
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}

        {filteredDatasets.length === 0 && (
          <Card className="border-dashed">
            <CardContent className="pt-6 text-center">
              <Database className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No datasets match your filters</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default NeMoDatasetManager;
